import React from 'react';
import './AppleSeasonInsights.css';

const AppleSeasonInsights = ({ dynamicOverview, playerName, season }) => {
    if (!dynamicOverview) {
        return (
            <div className="apple-insights-empty">
                <div className="empty-state">
                    <div className="empty-icon">📈</div>
                    <h3>Season Insights Unavailable</h3>
                    <p>Dynamic overview not available for {playerName} in {season}.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="apple-season-insights">
            {/* Hero Section */}
            <div className="insights-hero">
                <div className="hero-content">
                    <div className="player-info">
                        {dynamicOverview.player_visuals?.photo_url && (
                            <img 
                                src={dynamicOverview.player_visuals.photo_url} 
                                alt={playerName}
                                className="player-photo"
                            />
                        )}
                        <div className="player-details">
                            <h1>{playerName}</h1>
                            <p>{season} Season Journey</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Season Story */}
            {dynamicOverview.adaptive_summary && (
                <div className="story-section">
                    <div className="section-header">
                        <h2>📝 Season Story</h2>
                    </div>
                    <div className="story-card">
                        <p className="story-text">{dynamicOverview.adaptive_summary}</p>
                    </div>
                </div>
            )}

            {/* Evolution Story */}
            {dynamicOverview.evolution_story?.detected && (
                <div className="evolution-section">
                    <div className="section-header">
                        <h2>🚀 Player Evolution</h2>
                    </div>
                    <div className="evolution-card">
                        <div className="evolution-type">
                            {dynamicOverview.evolution_story.type?.replace('_', ' ').toUpperCase()}
                        </div>
                        <p className="evolution-description">
                            {dynamicOverview.evolution_story.description}
                        </p>
                    </div>
                </div>
            )}

            {/* Peak Performance Windows */}
            {dynamicOverview.peak_periods && dynamicOverview.peak_periods.length > 0 && (
                <div className="peak-periods-section">
                    <div className="section-header">
                        <h2>⭐ Peak Performance Windows</h2>
                        <p>When {playerName} was at their absolute best</p>
                    </div>
                    <div className="periods-grid">
                        {dynamicOverview.peak_periods.map((period, idx) => (
                            <PeakPeriodCard key={idx} period={period} index={idx} />
                        ))}
                    </div>
                </div>
            )}

            {/* Key Insights */}
            {dynamicOverview.contextual_insights && dynamicOverview.contextual_insights.length > 0 && (
                <div className="insights-section">
                    <div className="section-header">
                        <h2>💡 Key Insights</h2>
                        <p>What made this season special</p>
                    </div>
                    <div className="insights-grid">
                        {dynamicOverview.contextual_insights.map((insight, idx) => (
                            <InsightCard key={idx} insight={insight} />
                        ))}
                    </div>
                </div>
            )}

            {/* Season Statistics */}
            {dynamicOverview.dynamic_stats && (
                <div className="stats-section">
                    <div className="section-header">
                        <h2>📊 Season Statistics</h2>
                    </div>
                    <div className="stats-grid">
                        {dynamicOverview.dynamic_stats.season_averages && (
                            <StatCard
                                title="Season Averages"
                                icon="📈"
                                content={
                                    <div className="stat-values">
                                        <div className="primary-stat">
                                            {dynamicOverview.dynamic_stats.season_averages.pts?.toFixed(1)}
                                            <span className="stat-unit">PPG</span>
                                        </div>
                                        <div className="secondary-stats">
                                            <span>{dynamicOverview.dynamic_stats.season_averages.reb?.toFixed(1)} RPG</span>
                                            <span>{dynamicOverview.dynamic_stats.season_averages.ast?.toFixed(1)} APG</span>
                                        </div>
                                    </div>
                                }
                            />
                        )}
                        
                        {dynamicOverview.dynamic_stats.scoring_frequency && (
                            <StatCard
                                title="Scoring Frequency"
                                icon="🔥"
                                content={
                                    <div className="stat-values">
                                        <div className="primary-stat">
                                            {dynamicOverview.dynamic_stats.scoring_frequency['20_plus_games']}
                                            <span className="stat-unit">games</span>
                                        </div>
                                        <div className="secondary-stats">
                                            <span>{dynamicOverview.dynamic_stats.scoring_frequency.percentage_20plus?.toFixed(1)}% with 20+ pts</span>
                                        </div>
                                    </div>
                                }
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const PeakPeriodCard = ({ period, index }) => {
    const gradients = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    ];

    return (
        <div 
            className="peak-period-card"
            style={{ background: gradients[index % gradients.length] }}
        >
            <div className="period-dates">
                {period.start_date} - {period.end_date}
            </div>
            <div className="period-description">
                {period.description}
            </div>
            <div className="period-score">
                Score: {period.score?.toFixed(1)}
            </div>
        </div>
    );
};

const InsightCard = ({ insight }) => {
    return (
        <div className="insight-card">
            <div className="insight-title">{insight.title}</div>
            <div className="insight-description">{insight.description}</div>
        </div>
    );
};

const StatCard = ({ title, icon, content }) => {
    return (
        <div className="stat-card">
            <div className="stat-header">
                <span className="stat-icon">{icon}</span>
                <span className="stat-title">{title}</span>
            </div>
            <div className="stat-content">
                {content}
            </div>
        </div>
    );
};

export default AppleSeasonInsights;
